import { Link } from 'react-router-dom';
import {
  frankfurtArticle,
  frankfurtStats,
  frankfurtSections,
  frankfurtAtAGlance,
  frankfurtPhotos,
  frankfurtCollageIntro,
} from '@/mocks/frankfurt2026';
import ArticleHero from './components/ArticleHero';
import ArticleGallery from './components/ArticleGallery';
import FeaturedTitles from './components/FeaturedTitles';
import BoothCollage from './components/BoothCollage';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';

export default function FrankfurtBookFair2026() {
  const storyAnim = useScrollAnimation({ threshold: 0.05 });
  const collageAnim = useScrollAnimation({ threshold: 0.05 });
  const titlesAnim = useScrollAnimation({ threshold: 0.1 });
  const galleryAnim = useScrollAnimation({ threshold: 0.05 });
  const ctaAnim = useScrollAnimation({ threshold: 0.2 });

  return (
    <main>
      <Seo {...pageSeo.frankfurt2026} type="article" />
      <ArticleHero />

      {/* ============ STAT STRIP ============ */}
      <section className="w-full bg-white border-b border-cream-200">
        <div className="w-full px-4 md:px-6 lg:px-8 py-8 md:py-10">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-5 gap-y-6 gap-x-4">
            {frankfurtStats.map((stat, index) => (
              <div
                key={stat.label}
                className={`text-center ${
                  index !== frankfurtStats.length - 1 ? 'md:border-r md:border-cream-200' : ''
                }`}
              >
                <p className="text-2xl md:text-3xl font-bold text-navy-900 leading-none mb-2">
                  {stat.value}
                </p>
                <p className="text-[11px] md:text-xs text-navy-500 uppercase tracking-wider">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ ARTICLE BODY ============ */}
      <section id="the-story" className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={storyAnim.ref}
            className={`max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-10 lg:gap-14 animate-on-scroll ${
              storyAnim.isVisible ? 'animate-visible' : ''
            }`}
          >
            {/* Main column */}
            <article className="min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-gold-50 border border-gold-200 rounded-full text-[11px] font-semibold text-gold-700 uppercase tracking-wider">
                  <i className="ri-article-line"></i>
                  Event Report
                </span>
                <span className="text-xs text-navy-500">Published {frankfurtArticle.published}</span>
              </div>

              <h2 className="text-2xl md:text-3xl font-bold text-navy-900 leading-snug mb-6">
                {frankfurtArticle.subtitle}
              </h2>

              <p className="text-base md:text-lg text-navy-700 leading-relaxed mb-10 pb-10 border-b border-cream-200">
                {frankfurtArticle.excerpt}
              </p>

              <div className="space-y-10">
                {frankfurtSections.map((section, index) => (
                  <div key={section.heading}>
                    <h3 className="flex items-start gap-3 text-lg md:text-xl font-bold text-navy-900 mb-4">
                      <span className="mt-1 w-1.5 h-1.5 rounded-full bg-gold-400 shrink-0"></span>
                      {section.heading}
                    </h3>
                    <div className="space-y-4 pl-4 md:pl-5 border-l-2 border-cream-200">
                      {section.paragraphs.map((paragraph) => (
                        <p key={paragraph.slice(0, 40)} className="text-sm md:text-[15px] text-navy-700 leading-relaxed">
                          {paragraph}
                        </p>
                      ))}
                    </div>

                    {index === 1 && (
                      <div className="mt-8 bg-navy-900 rounded-2xl p-6 md:p-8 relative overflow-hidden">
                        <div className="absolute -top-16 -right-10 w-48 h-48 bg-gold-500/10 rounded-full blur-2xl"></div>
                        <i className="ri-double-quotes-l text-gold-400 text-2xl"></i>
                        <p className="relative text-sm md:text-base text-cream-100 leading-relaxed mt-3">
                          {frankfurtArticle.pullQuote}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-12 pt-8 border-t border-cream-200 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <Link
                  to="/events"
                  className="inline-flex items-center gap-2 text-sm font-medium text-navy-600 hover:text-gold-600 transition-colors duration-300"
                >
                  <i className="ri-arrow-left-line"></i>
                  Back to all events
                </Link>
                <a
                  href={frankfurtPhotos[0].src}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-medium text-navy-600 hover:text-gold-600 transition-colors duration-300"
                >
                  <i className="ri-image-line"></i>
                  View the lead photo full size
                </a>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="min-w-0">
              <div className="lg:sticky lg:top-24 space-y-5">
                <div className="bg-white rounded-2xl border border-cream-200 p-6">
                  <h3 className="text-sm font-bold text-navy-900 uppercase tracking-wider mb-4">
                    At a glance
                  </h3>
                  <dl className="space-y-3">
                    {frankfurtAtAGlance.map((row) => (
                      <div key={row.label} className="flex items-start justify-between gap-3">
                        <dt className="text-xs text-navy-500 shrink-0">{row.label}</dt>
                        <dd className="text-xs font-medium text-navy-900 text-right">{row.value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>

                <div className="rounded-2xl overflow-hidden border border-cream-200 bg-white">
                  <div className="w-full aspect-[4/3] overflow-hidden">
                    <img
                      src={frankfurtPhotos[5].src}
                      alt={frankfurtPhotos[5].caption}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-5">
                    <p className="text-xs text-navy-500 leading-relaxed mb-4">
                      {frankfurtPhotos[5].caption}
                    </p>
                    <p className="text-sm font-bold text-navy-900 mb-1">Did we meet in Frankfurt?</p>
                    <p className="text-xs text-navy-600 leading-relaxed mb-4">
                      Rights enquiries from the fair are being handled now. Send us the title you asked about and
                      we will pick the thread back up.
                    </p>
                    <Link
                      to="/contact-us"
                      className="inline-flex items-center justify-center gap-2 w-full px-5 py-2.5 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                    >
                      Contact our team
                      <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ============ CLOSE-UP COLLAGE ============ */}
      <section className="w-full py-14 md:py-20 bg-navy-950 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 -left-24 w-72 h-72 bg-gold-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 -right-24 w-80 h-80 bg-navy-500/10 rounded-full blur-3xl"></div>
        </div>
        <div className="relative w-full px-4 md:px-6 lg:px-8">
          <div
            ref={collageAnim.ref}
            className={`max-w-6xl mx-auto animate-on-scroll ${collageAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-9">
              <div>
                <p className="text-xs font-medium text-gold-400 uppercase tracking-[0.2em] mb-3">
                  {frankfurtCollageIntro.eyebrow}
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-white">
                  {frankfurtCollageIntro.heading}
                </h2>
                <div className="w-16 h-1 bg-gold-400 rounded-full mt-4"></div>
              </div>
              <p className="text-sm text-cream-200/80 max-w-sm leading-relaxed">
                {frankfurtCollageIntro.description}
              </p>
            </div>

            <BoothCollage />
          </div>
        </div>
      </section>

      {/* ============ FEATURED TITLES ============ */}
      <section className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={titlesAnim.ref}
            className={`max-w-6xl mx-auto animate-on-scroll ${titlesAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-5 mb-9">
              <div>
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">
                  From the Poster Wall
                </p>
                <h2 className="text-2xl md:text-3xl font-bold text-navy-900">
                  The titles that carried the booth
                </h2>
                <div className="w-16 h-1 bg-gold-400 rounded-full mt-4"></div>
              </div>
              <p className="text-sm text-navy-600 max-w-sm leading-relaxed">
                Six of the covers visitors reached for first, across fiction, western, and fantasy.
              </p>
            </div>
            <FeaturedTitles />
          </div>
        </div>
      </section>

      {/* ============ PHOTO GALLERY ============ */}
      <section className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div
            ref={galleryAnim.ref}
            className={`max-w-6xl mx-auto animate-on-scroll ${galleryAnim.isVisible ? 'animate-visible' : ''}`}
          >
            <div className="text-center mb-10">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">
                The Booth in Pictures
              </p>
              <h2 className="text-2xl md:text-3xl font-bold text-navy-900">
                Frankfurt, five days in ten frames
              </h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
              <p className="text-sm text-navy-600 mt-4 max-w-2xl mx-auto leading-relaxed">
                Shot at stand A104 between visitors. Click any photo to open it, then use the arrow keys to move
                through the set.
              </p>
            </div>
            <ArticleGallery />
          </div>
        </div>
      </section>

      {/* ============ CTA ============ */}
      <section className="w-full py-14 md:py-20 bg-navy-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-72 h-72 bg-gold-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-navy-400/10 rounded-full blur-3xl"></div>
        </div>
        <div
          ref={ctaAnim.ref}
          className={`relative w-full px-4 md:px-6 lg:px-8 animate-on-scroll ${ctaAnim.isVisible ? 'animate-visible' : ''}`}
        >
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/15 border border-gold-400/30 rounded-full mb-6">
              <i className="ri-earth-line text-gold-400 text-sm"></i>
              <span className="text-[11px] font-medium text-gold-300 uppercase tracking-[0.2em]">
                Next Stop: The Trade Calendar
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-5 leading-snug">
              Want your book on the wall in <span className="text-gold-400">2027?</span>
            </h2>
            <p className="text-sm md:text-base text-cream-200 leading-relaxed mb-8 max-w-2xl mx-auto">
              Booth space at international fairs is planned months ahead. If you want your title on the next
              Leavitt Peak Press wall, start the conversation early.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact-us"
                className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Reserve Booth Space
              </Link>
              <Link
                to="/events"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                See All Events
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}